/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import CONFIG from 'config';
import 'core-js/stable';
import $ from 'jquery';
import { BehaviorSubject, Observable, from, merge, of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  delay,
  filter,
  tap,
} from 'rxjs/operators';
import 'select2';
import 'whatwg-fetch';
import * as Api from '../src/utils/api';
import * as App from './app/app';
import { errorHandler } from './app/errorHandler';
import * as AppOptions from './app/options';
import { ApplicationException } from './exceptions/application/ApplicationException';
import { LogRequest } from './models/LogsRequest';
import { MessageDetails } from './models/MessageDetails';
import { PermissionDialogResult } from './models/PermissionDialogResult';
import './scss/main.scss';
import * as Auth from './utils/authUtils';
import { loaderDetails$ } from './utils/loaderUtil';
import {
  addToExistingErrorLogs,
  addToExistingInfoLogs,
  clearErrorLogs,
  clearInfoLogs,
  errorLogsChange$,
  infoLogsChange$,
} from './utils/logUtils';
import { Authenticator } from './utils/office-js-helpers/Authenticator';
import * as ProductAnalytics from './utils/product-analytics';
import { sleep } from './utils/sleep';
import * as Ui from './utils/uiUtils';
import * as UserSettings from './utils/user-settings';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).$ = (window as any).jQuery = $;

const TAG = 'IDX3: ';

let selectProjectIds = [];
let selectDomain = '';
let selectProject = '';
/** handleProjectChanged is triggered by refreshDomains() in showStakeholdersForm().
 * because handleProjectChanged can take longer than showStakeholdersForm(),
 * showStakeholdersForm() might hide the loader before handleProjectChanged is finished.
 * this flag is to fix this
 */
let processingProjectChange = false;
let isProjectOptionsUpdated = false;

Office.initialize = handleOfficeInitialize;
const _triggerProjectChangeSubject = new BehaviorSubject(false);
_triggerProjectChangeSubject.pipe(debounceTime(200)).subscribe((v) => {
  if (v) {
    $('#save-project').trigger('change');
  }
});

async function handleOfficeInitialize() {
  registerLogSubscription();
  console.log(TAG, 'SSTK Outlook initializing...');
  if (Authenticator.isAuthDialog()) return;

  console.log(TAG, 'SSTK Outlook initializing authentication...');

  const version = 'Version 2022-05-10 13:41';

  console.log(TAG, `Version: ${version}`);

  if (CONFIG.enableDemoAPIRegion) {
    $('#version').html(version);
    $('#version').show();
  }

  $('#app-body').show();
  Ui.setAppName(CONFIG.appName);
  Ui.setFreeTrialUrl(CONFIG.freeTrialUrl);
  Ui.setIcon(CONFIG.appIcon, CONFIG.appIconWidth);
  Ui.setOptions(
    CONFIG.isComplaintsEnabled,
    CONFIG.isConfidentialOptionsEnabled
  );
  UserSettings.setAutosave(true);
  Ui.showRegions(CONFIG.regions);
  selectDomain = UserSettings.getDomain();

  // A previously-selected region (e.g. UK) may no longer be offered in CONFIG.regions
  // once it's removed (region decommissioned/migrated). Clear the stale domain and
  // any tokens issued against it so we don't keep silently calling a retired API,
  // and force the user back through region selection + sign-in instead.
  if (selectDomain && !CONFIG.regions.some((r) => r.api_url === selectDomain)) {
    console.log(
      TAG,
      'Stored domain is no longer a supported region, clearing',
      selectDomain
    );
    UserSettings.clearDomainAndAuth();
    selectDomain = '';
  }

  $('#open-login-pg').click(handleSignInClick);

  // NOTE: asynchronous token checks should be performed after all events are hooked,
  // otherwise UI can be unresponsive and Permission Dialog can be messed up
  if (Auth.isTokenExpired()) {
    try {
      await Auth.refreshToken();
    } catch (error) {
      // no need to process this error because we'll check if token is valid in next function
      console.log(TAG, 'error refreshing token ', JSON.stringify(error));
    }
  }

  console.log(TAG, `Selected Outlook Item Changed initializing...`);

  await handleSelectedOutlookItemChanged();
}

Office.onReady(function () {
  // Office is ready
  $(document).ready(function () {
    handleDocumentReady();
  });
});

function handleDocumentReady() {
  Office.context.mailbox.addHandlerAsync(
    Office.EventType.ItemChanged,
    handleSelectedOutlookItemChanged
  );

  $('#item-contact-group').select2({ width: 'style' });
  $('#item-interaction-type').select2({ width: 'style' });
  $('#item-tag').select2({ width: 'style' });
  $('#save-project').select2({
    multiple: true,
    placeholder: 'Select Project',
    width: 'style',
  });

  $('#logout').on('click', handleLogoutClick);
  $('#login-pop-close').click(handleLoginConfirmClick);
  $('#detail-pop-close, #detail-pop-close2').click(handleSaveCancelClick);

  // Initialize admin settings gear icon
  if (CONFIG.isNewSstkAutosaveContactEnabled) {
    $('#admin-settings-gear').show();
    Ui.initializeAdminSettingsGear();
  }

  // Detail Page Scripts
  $(document).on(
    'change',
    '#select-individual-main',
    handleStakeholderTypeChange
  );
  $(document).on(
    'change',
    '.cc-bcc-single-details select',
    handleAdditionalStakeholderTypeChanged
  );
  $('#save-email-detail').click(handleSaveClick);
  $('#save-email-detail2').click(handleSaveClick);
  $('#detail-save-pop-close').click(handleSavedDialogCloseClick);
  $('#detail-save-error-close').click(handleSessionExpiredDialogOkClick);
  $('#error__btn-ok').click(handleErrorDialogCloseClick);
  $('#error__btn-details').click(handleErrorDialogDetailsClick);
  $('#dialog-info-btn-ok').click(handleInfoDialogOkClick);
  $('#dialog-logout-success-btn-ok').click(handleLogoutSuccessDialogOkClick);
  $('#save-proj-group').on('change', handleProjectGroupChange);
  $('#save-domain').on('change', handleDomainChange);
  $('#save-project').on('change', handleProjectChanged);
  $('#item-assigned-to').on('change', handleItemAssignedToChange);
  $('#complaint-priorities').on('change', handleComplaintPriorityChange);
  $('input[name="AutoSave"]').on('click', handleAutosaveOptionClick);
  $('#add-to-darzin').on('click', handleAutosaveOptionChosen);
  $('#item-communication').on('change', handleEmailTypeChange);
  $('#save-client').on('change', handleClientChange);
  $('#futureAllTooltip').on('click', handleFutureAllTooltipClick);
}

async function handleSelectedOutlookItemChanged() {
  $('#loader').show();

  console.log(
    TAG,
    'Selected Outlook Item Changed: ',
    Office.context.mailbox.item
      ? Office.context.mailbox.item.subject
      : 'undefined'
  );

  // Check if token still valid, if not then refresh the token.
  // This will prevent the subsequent endpoints from failing.
  // When the add-in is first initialising, the refresh is expected to fail because authentication will happen on the login page.
  if (!Auth.isTokenValid()) {
    try {
      await Auth.refreshToken();
    } catch (error) {
      console.log(TAG, 'error refreshing token ', JSON.stringify(error));
    }
    if (!Auth.isTokenValid()) {
      $('#loader').hide();
      Ui.hidePages();
      $('#welcome-pg').show();
      return;
    }
  }

  const item = Office.context.mailbox.item;
  $('#loader').show();
  Ui.hideSaveSuccessfulDialog();
  Ui.hidePages();

  // Clear per-email user selections immediately so stale values from the
  // previous email are never visible when the form is shown for the new one.
  // (These fields are only re-populated in handleProjectChanged, which only
  // fires when the project changes - so same-project email switches would
  // otherwise keep showing the old selections indefinitely.)
  $('#item-tag').val(null).trigger('change');
  $('#item-confidential').val('');
  $('#item-contact-group').val(null).trigger('change');
  $('#item-interaction-type').val('1').trigger('change');

  await App.getUserContext();

  console.log(TAG, 'Getting all the client list');
  await App.getClientList();
  console.log(TAG, 'Preparing for Autosave migration...');
  await App.prepareAutosaveContactMigration();
  console.log(TAG, 'Migration Completed');
  console.log(TAG, 'Preparing for auto-sync of contacts');
  await App.startAutosaveTimer(item);
  console.log(TAG, 'Auto-sync of contacts has been completed');
  await App.tryStart(item);
  $('#loader').hide();
}

let permissionDialog: Office.Dialog;
function handleSignInClick() {
  const domainElement = $('#select-domain-login').val();

  if (!domainElement || !domainElement.toString()) return;

  console.log(TAG, 'Displaying permission dialog', domainElement);
  Office.context.ui.displayDialogAsync(
    CONFIG.urls.origin + 'permission-dialog.html',
    { height: 60, width: 40, promptBeforeOpen: false },
    onPermissionDialogLoad
  );
}

function onPermissionDialogLoad(
  asyncResult: Office.AsyncResult<Office.Dialog>
) {
  console.log(TAG, 'Permission dialog has loaded');
  try {
    if (asyncResult.error) {
      throw new ApplicationException(
        'An error occurred in Permission Dialog',
        asyncResult.error
      );
    } else if (!asyncResult.value) {
      throw new ApplicationException(
        'An error occurred in Permission Dialog: invalid reference to the dialog',
        asyncResult.error
      );
    } else {
      permissionDialog = asyncResult.value;
      permissionDialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        onPermissionDialogResult
      );
    }
  } catch (error) {
    errorHandler(error, 'Failed to load permission dialog');
  }
}

async function onPermissionDialogResult(arg) {
  console.log(TAG, 'Processing Permission Dialog result', arg);
  try {
    permissionDialog.close();
    // wait for dialog.close to be processed, otherwise sign-in dialog wouldn't open
    await sleep(500);
    const permissionDialogResult = JSON.parse(
      arg.message
    ) as PermissionDialogResult;

    if (permissionDialogResult.permissionGranted) {
      handleSignInPermissionAllowedClick();
    }
  } catch (error) {
    errorHandler(error, 'Failed to process permission dialog result');
  }
}

async function handleSignInPermissionAllowedClick() {
  console.log(TAG, 'Permissions were granted to the app, signing in...');
  $('#loader').show();
  try {
    $('#open-login-pg-error').html('');

    selectDomain = $('#select-domain-login').val().toString();
    // remove extra '/': it might cause request errors in some browsers? not confirmed
    selectDomain += '/';
    if (selectDomain.endsWith('/')) {
      selectDomain = selectDomain.slice(0, selectDomain.length - 1);
    }
    console.log(TAG, 'Setting domain ', selectDomain);
    UserSettings.setDomain(selectDomain);

    if (!selectDomain) {
      $('#select-domain-login-error').html('Please Select Region');
      $('#select-domain-login').focus();
      return;
    }

    // REF: https://github.com/OfficeDev/office-js-helpers#authentication
    const authenticator = new Authenticator({
      baseUrl: selectDomain,
      authorizeUrl: '/authorize',
      clientId: CONFIG.auth.client_id,
      redirectUrl: CONFIG.urls.form,
      scope: 'openid offline_access',
      responseType: 'code',
      extraQueryParameters: {
        login_hint: Office.context.mailbox.userProfile.emailAddress,
      },
    });

    const response = await authenticator.authenticate();
    await Auth.getToken(selectDomain, response.code);

    await Auth.refreshClientCode();

    // because token can't be refreshed if project isn't selected,
    // we'll select first available project and refresh token for it
    const projectId = await App.selectAnyProjectId();
    if (projectId) {
      UserSettings.setProject(projectId);
      await Auth.refreshToken();
    }

    await App.getUserContext();

    await ProductAnalytics.trackEvent({
      eventName: 'outlook_logged_in',
      data: {
        addInType: CONFIG.isGraphApiEnabled ? 'normal' : 'restricted',
      },
    });
  } catch (error) {
    if (error.innerError == '12006') {
      console.log(TAG, 'Dialog was closed');
    } else {
      errorHandler(error, 'Authorization failed');
    }
    $('#loader').hide();
    return;
  }

  await handleSelectedOutlookItemChanged();
  $('#loader').hide();
}

function handleLoginConfirmClick() {
  $('#login-pop').hide();
  $('#login-page').hide();
  $('#detail-page').show();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleAutosaveOptionClick(e: any) {
  UserSettings.setAutosave(e.target.value === 'auto_save');
  await handleAutosaveOptionChosen();
}

async function handleAutosaveOptionChosen() {
  $('#detail-page').hide();
  $('#detail-final').show();

  $('#loader').show();

  // Check if token still valid, if not then refresh the token.
  // This will prevent the subsequent endpoints from failing.
  if (!Auth.isTokenValid()) {
    await Auth.refreshToken();
  }

  // this will trigger handleProjectChanged,
  // which will refresh project options
  try {
    await App.refreshClients();
  } catch (error) {
    errorHandler(error, `Failed to refresh client's domain list`);
  }

  // if domains or project groups are disabled, handleProjectChanged() will not be triggered by refreshDomains()
  // trigger it here so project options and token will be updated
  if (!isProjectOptionsUpdated) {
    _triggerProjectChangeSubject.next(true);
  }
  const item = Office.context.mailbox.item;
  if (item) {
    await App.showStakeholdersForm(item);
  }
  if (!processingProjectChange) {
    $('#loader').hide();
  }
}

async function handleClientChange(event) {
  // deselect because otherwise will interfere with mouse wheel scroll
  $('#save-client').blur();

  const clientId = event.target.value;

  $('#loader').show();

  try {
    UserSettings.setClientCode(
      App.clients.find((c) => c.ClientID == clientId)?.Name
    );

    // Check if token still valid, if not then refresh the token.
    // This will prevent the subsequent endpoints from failing.
    if (!Auth.isTokenValid()) {
      await Auth.refreshToken();
    }

    await App.refreshDomains(clientId);
  } catch (error) {
    errorHandler(error, 'Failed to get domain options');
  }

  $('#loader').hide();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleDomainChange(event: any) {
  const domainId = event.target.value;
  const selectedDomain = App.domains.find(function (domain) {
    return domain.ID == domainId;
  });
  if (selectedDomain) {
    $('#save-proj-group').html('');
    $('#save-project').html('');
    if (App.projectGroupAvailable) {
      $('#ProjectGroupContainer').show();
      let projectGroupOption = '';
      const singleProjectGroup = selectedDomain.ProjectGroups.length === 1;

      for (let j = 0; j < selectedDomain.ProjectGroups.length; j++) {
        const group = selectedDomain.ProjectGroups[j];
        // skip groups without accessible projects
        if (!group.Projects || !group.Projects.length) continue;
        projectGroupOption +=
          '<option value="' +
          group.ID +
          '">' +
          group.ProjectGroupName +
          '</option>';
      }
      $('#save-proj-group')
        .select2({
          multiple: true,
          placeholder: 'Select Project Group',
        })
        .html(projectGroupOption);
      // if there is only one group, select it.
      if (singleProjectGroup) {
        $('#save-proj-group').trigger('change');
      } else {
        // if there are multiple groups, prompt user to select project
        const emptyProjectOption =
          '<option value="" disabled selected>Select Project</option>';
        $('#save-project').html(emptyProjectOption);
      }
    } else {
      $('#ProjectGroupContainer').hide();
      let projectOption = '';
      const singleProject = selectedDomain.Projects.length === 1;
      for (let j = 0; j < selectedDomain.Projects.length; j++) {
        const dataProject = selectedDomain.Projects[j];
        projectOption +=
          '<option value="' +
          dataProject.ID +
          '">' +
          dataProject.ProjectName +
          '</option>';
      }
      $('#save-project').html(projectOption);
      if (singleProject) {
        _triggerProjectChangeSubject.next(true);
      }
    }
  }
}

async function handleProjectChanged(event) {
  // deselect because otherwise will interfere with mouse wheel scroll
  $('#save-project').blur();

  const formSelectedProjectIds = $(event.target)
    .select2('data')
    ?.map((i) => i.id);

  formSelectedProjectIds.forEach((id) => {
    if (id && !selectProjectIds.includes(id)) {
      selectProjectIds.push(id);
    }
  });

  selectProjectIds = selectProjectIds.filter((id) =>
    formSelectedProjectIds.includes(id)
  );

  // if no project is selected, we can't refresh tokens
  // if (CONFIG.appName === 'Darzin')
  if (!selectProjectIds || selectProjectIds.length == 0) return;

  processingProjectChange = true;

  $('#loader').show();

  const firstProject = selectProjectIds[0];
  if (selectProject != firstProject) {
    selectProject = firstProject;

    UserSettings.setProject(firstProject);

    try {
      await Auth.refreshToken();

      const optionPromises = [AppOptions.refreshContactGroups()];
      if (CONFIG.isConfidentialOptionsEnabled) {
        optionPromises.push(AppOptions.refreshConfidentialities());
        optionPromises.push(AppOptions.refreshUsers());
        optionPromises.push(AppOptions.refreshPriorities());
        optionPromises.push(AppOptions.refreshComplaintStatuses());
      }
      optionPromises.push(AppOptions.refreshInteractionTypes());
      optionPromises.push(AppOptions.refreshInteractionTags());
      await Promise.all(optionPromises);
      $('#item-interaction-type').val('1').trigger('change');
    } catch (error) {
      errorHandler(error, 'Failed to get project options');
    }
  }
  $('#project-error').html('');
  $('#loader').hide();
  processingProjectChange = false;
  isProjectOptionsUpdated = true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleProjectGroupChange(event: any) {
  $('#loader').show();
  $('#project-group-error').html('');
  let projectOption = '';
  const values = $(event.target)
    .select2('data')
    .map((i) => i.id);
  let defaultProject = null;
  App.domains.forEach(function (domain) {
    const selectedProjectGroups = domain.ProjectGroups.filter(function (
      projectGroup
    ) {
      return values.includes(projectGroup.ID);
    });
    if (selectedProjectGroups) {
      if (
        selectedProjectGroups.length == 1 &&
        selectedProjectGroups[0].Projects.length === 1
      ) {
        defaultProject = selectedProjectGroups[0].Projects[0].ID;
      }
      selectedProjectGroups.forEach(function (pg) {
        pg.Projects.forEach(function (project) {
          projectOption +=
            '<option value="' +
            project.ID +
            '">' +
            project.ProjectName +
            '</option>';
        });
      });
    }
  });
  $('#loader').hide();
  $('#save-project').html(projectOption);
  $('#save-project').val(defaultProject);
  if (defaultProject) {
    _triggerProjectChangeSubject.next(true);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleEmailTypeChange(e: any) {
  if (e.target.value == 'Communication') {
    $('.communication-method').show();
    $('.item-assigned-to-main').hide();
    $('.complaint-priorities-main').hide();
    $('.complaint-statuses-main').hide();
  } else {
    // $('.communication-method').hide()
    $('.item-assigned-to-main').show();
    $('#item-assigned-to-error').html('');
    $('#complaint-priorities-error').html('');
    if ($('#complaint-priorities').html()) {
      $('.complaint-priorities-main').show();
      $('.complaint-statuses-main').show();
    } else {
      $('.complaint-priorities-main').hide();
      $('.complaint-statuses-main').hide();
    }
  }
}

function handleItemAssignedToChange() {
  $('#item-assigned-to-error').html('');
}

function handleComplaintPriorityChange() {
  $('#complaint-priorities-error').html('');
}

function handleStakeholderTypeChange() {
  if ($(this).find(':selected').val() == 'Individual') {
    $('#select-individual-main-detail').show();
    $('#item-org2').parent().parent('.form-group').hide();
  } else {
    $('#select-individual-main-detail').hide();
    $('#item-org2').parent().parent('.form-group').show();
  }
}

function handleAdditionalStakeholderTypeChanged() {
  const pTable = $(this).parent().parent().parent().parent(); // prettier-ignore
  if ($(this).find(':selected').val() == 'Individual') {
    pTable.find('.table-indi-fields').show();
    pTable.find('.table-org-fields').hide();
  } else {
    pTable.find('.table-indi-fields').hide();
    pTable.find('.table-org-fields').show();
  }
}

async function handleSaveClick() {
  if (!Ui.checkSaveForm()) {
    return;
  }

  const item = Office.context.mailbox.item;
  if (item) {
    $('#loader').show();

    // Check if token still valid, if not then refresh the token.
    // This will prevent the subsequent endpoints from failing.
    if (!Auth.isTokenValid()) {
      await Auth.refreshToken();
    }

    await App.trySaveMessage(item);
    $('#loader').hide();
  }
}

function handleSaveCancelClick() {
  $('#detail-final').hide();
  $('#detail-page').show();
}

function handleLogoutClick() {
  App.logout();
}

function handleErrorDialogCloseClick() {
  $('#error-dialog').hide();
}

function handleErrorDialogDetailsClick() {
  $('#error__message-details').show();
  $('#error__btn-details').hide();
}

function handleInfoDialogOkClick() {
  Ui.hideInfoDialog();
}

function handleLogoutSuccessDialogOkClick() {
  $('#loader').show(); // will be reset to hidden after window reloaded
  $('#dialog-logout-success').hide();
  window.location.reload();
}

async function handleSavedDialogCloseClick() {
  Ui.hideSaveSuccessfulDialog();
  Ui.hideDocumentDetailsPage();

  await handleSelectedOutlookItemChanged();
}

function handleSessionExpiredDialogOkClick() {
  $('#loader').show(); // will be reset to hidden after window reloaded
  $('#detail-save-error').hide();
  window.location.reload();
}

function handleFutureAllTooltipClick() {
  Ui.showInfoDialog($(this).attr('title'));
}

function registerLogSubscription(): void {
  const handleLogs = (
    logChange$: Observable<LogRequest>,
    saveLogs: (request: LogRequest) => Promise<unknown>,
    clearLogs: () => void,
    addToExistingLogs: (logs: string[]) => void
  ) => {
    return logChange$.pipe(
      debounceTime(2000),
      filter((request) => !!request),
      tap(() => clearLogs()),
      concatMap((request) => {
        return from(saveLogs(request)).pipe(
          catchError(() => {
            addToExistingLogs(request.Logs);
            return of('error');
          })
        );
      }),
      catchError(() => of('error'))
    );
  };

  const infoLogs$ = handleLogs(
    infoLogsChange$,
    Api.LogInfo,
    clearInfoLogs,
    addToExistingInfoLogs
  );

  const errorLogs$ = handleLogs(
    errorLogsChange$,
    Api.LogError,
    clearErrorLogs,
    addToExistingErrorLogs
  );

  const showInfoInLoader$ = loaderDetails$.pipe(
    concatMap((log) => {
      // Return a new Observable that delays updating the loaderDetails
      return of(log).pipe(
        delay(25), // Adjust delay as needed
        tap((log) => {
          const loaderDetails = $('#loader .loader-details');
          const loaderReminder = $('#loader .loader-reminder');
          if (typeof log === 'string' || !log) {
            loaderDetails.html((log as string) || 'Loading');
            loaderReminder.html('');
          } else if (typeof log === 'object') {
            const msg = log as MessageDetails;
            loaderDetails.html(msg.text);
            loaderReminder.html(msg.secondaryText);
          }
        })
      );
    })
  );

  merge(infoLogs$, errorLogs$, showInfoInLoader$).subscribe();
}
